import { useState, useEffect } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, ChevronLeft, ChevronRight, Shield, User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { motion } from "framer-motion";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { UserDetailPanel } from "@/components/dashboard/admin/UserDetailPanel";

type UserDetails = {
  id: string;
  email: string;
  username: string | null;
  name: string | null;
  last_sign_in_at: string | null;
  created_at: string;
  isAdmin: boolean;
};

const AdminUsersList = () => {
  const [users, setUsers] = useState<UserDetails[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserDetails | null>(null);
  const { toast } = useToast();
  const usersPerPage = 10;

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch profiles
      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('id, email, username, name, created_at, last_sign_in_at')
        .order('created_at', { ascending: false });
      
      if (profileError) {
        throw profileError;
      }

      // Fetch user roles
      const { data: userRoles, error: rolesError } = await supabase
        .from('user_roles')
        .select('user_id, role')
        .eq('role', 'admin');
      
      if (rolesError) {
        throw rolesError;
      }

      // Convert to UserDetails type
      const adminUserIds = new Set((userRoles || []).map(role => role.user_id));
      const userDetails: UserDetails[] = (profiles || []).map(profile => ({
        id: profile.id as string,
        email: profile.email as string,
        username: profile.username as string | null,
        name: profile.name as string | null,
        last_sign_in_at: profile.last_sign_in_at as string | null,
        created_at: profile.created_at as string,
        isAdmin: adminUserIds.has(profile.id)
      }));

      setUsers(userDetails);
      setFilteredUsers(userDetails);
    } catch (err) {
      console.error("Error fetching users:", err);
      setError(err instanceof Error ? err.message : "Failed to load users");
      toast({
        title: "Error loading users",
        description: err instanceof Error ? err.message : "An unknown error occurred",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    let result = users;
    
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(user =>
        user.email.toLowerCase().includes(search) ||
        (user.username?.toLowerCase().includes(search) || false) ||
        (user.name?.toLowerCase().includes(search) || false)
      );
    }

    if (roleFilter !== "all") {
      result = result.filter(user => user.isAdmin === (roleFilter === "admin"));
    }

    setFilteredUsers(result);
  }, [users, searchTerm, roleFilter]);

  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const currentUsers = filteredUsers.slice(
    (currentPage - 1) * usersPerPage,
    currentPage * usersPerPage
  );

  const handleUserClick = (user: UserDetails) => {
    setSelectedUser(user);
  };

  const handleClearSelection = () => {
    setSelectedUser(null);
  };

  return (
    <div className="flex h-full">
      {/* Left Panel */}
      <div className="w-1/3 h-full border-r">
        <div className="h-full overflow-y-auto">
          <div className="p-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">Users</h2>
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-8"
                  />
                </div>
                <Select value={roleFilter} onValueChange={setRoleFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="admin">Admins</SelectItem>
                    <SelectItem value="user">Users</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="h-[calc(100vh-170px)] overflow-y-auto pr-2">
              <div className="space-y-4">
                {currentUsers.map((user) => (
                  <Button
                    key={user.id}
                    variant="outline"
                    className="w-full justify-start gap-4 hover:bg-gray-50"
                    onClick={() => handleUserClick(user)}
                  >
                    <div className="flex items-center gap-2">
                      <Avatar className="h-8 w-8">
                        <AvatarImage
                          src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || user.email)}`}
                          alt={user.name || user.email}
                        />
                        <AvatarFallback>
                          {user.name
                            ? user.name.split(' ').map(n => n[0]).join('').toUpperCase()
                            : user.email?.[0]?.toUpperCase() || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{user.name || user.email}</p>
                        <p className="text-sm text-gray-500">
                          {user.isAdmin ? "Admin" : "User"}
                        </p>
                      </div>
                    </div>
                  </Button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between px-2 py-4 border-t">
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-sm text-gray-500">
                Page {currentPage} of {totalPages}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 h-full overflow-hidden">
        <div className="h-full overflow-y-auto">
          <div className="p-6">
            {selectedUser ? (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.2 }}
              >
                <UserDetailPanel user={selectedUser} onClose={handleClearSelection} />
              </motion.div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold">User Details</h2>
                  <Button variant="outline" onClick={handleClearSelection}>
                    Clear Selection
                  </Button>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-500 text-center">
                    Select a user from the list on the left to view their details
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export { AdminUsersList };
